// Simple API client for calling the Lambda HTTP API
// Configure base URL via one of the following (priority order):
// 1) REACT_APP_API_BASE (build-time)
// 2) URL query ?api=https://xxx (runtime; persisted to localStorage)
// 3) window.__API_BASE__ (runtime global)
// 4) localStorage['API_BASE'] (runtime, persistent in browser)

function readQueryApi() {
  try {
    if (typeof window === 'undefined') return '';
    const usp = new URLSearchParams(window.location.search || '');
    const q = usp.get('api');
    if (!q) return '';
    // Persist for subsequent reloads
    try { window.localStorage?.setItem('API_BASE', q); } catch (_) {}
    return String(q);
  } catch (_) {
    return '';
  }
}

function getRuntimeBase() {
  try {
    // Priority: query param -> window global -> localStorage
    const fromQuery = readQueryApi();
    if (fromQuery) return fromQuery;
    if (typeof window !== 'undefined' && window.__API_BASE__) return String(window.__API_BASE__);
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('API_BASE');
      if (saved) return String(saved);
    }
  } catch (_) {}
  return '';
}

function resolveBase() {
  const envBase = (process.env.REACT_APP_API_BASE || '').trim();
  const runtimeBase = getRuntimeBase();
  const base = envBase || runtimeBase || '';
  return base.replace(/\/$/, '');
}

let warned = false;
function warnOnce(msg) {
  if (!warned) {
    // eslint-disable-next-line no-console
    console.warn(msg);
    warned = true;
  }
}

async function fetchJson(path, { method = 'GET', body, headers } = {}) {
  const BASE = resolveBase();
  if (!BASE) {
    warnOnce('API base URL is not configured. Set REACT_APP_API_BASE at build time, add ?api=<url> to the address bar (persisted), define window.__API_BASE__ at runtime, or set localStorage["API_BASE"]. Example: localStorage.setItem("API_BASE","https://xxxx.execute-api.<region>.amazonaws.com"); then reload.');
  }
  const isAbsolute = /^https?:\/\//i.test(path);
  const url = isAbsolute ? path : `${BASE}${path}`;
  // Build headers. Avoid forcing Content-Type on GET/HEAD requests to prevent CORS preflight.
  // Only set JSON Content-Type when we actually send a request body.
  const h = { ...(headers || {}) };
  const hasBody = body != null;
  if (hasBody && !h['Content-Type'] && !h['content-type']) {
    h['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers: h,
    body: hasBody ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
    credentials: 'omit',
  });

  // 204 no content
  if (res.status === 204) return null;

  // Prefer JSON, but detect HTML and provide a helpful error
  const contentType = String(res.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const json = await res.json();
    if (!res.ok) {
      const msg = (json && (json.message || json.error)) || `${res.status} ${res.statusText}`;
      const err = new Error(msg);
      err.status = res.status;
      err.body = json;
      throw err;
    }
    return json;
  }

  // Non-JSON: read as text
  const text = await res.text();
  if (!res.ok) {
    const err = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  // If HTML was returned (likely dev server index.html), surface a clear error
  if (/^\s*</.test(text)) {
    throw new Error('Received HTML instead of JSON from API. Check REACT_APP_API_BASE/window.__API_BASE__ and CORS.');
  }
  // Try to parse JSON from text fallback
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

const apiClient = {
  get: (p, headers) => fetchJson(p, { method: 'GET', headers }),
  post: (p, body, headers) => fetchJson(p, { method: 'POST', body, headers }),
  put: (p, body, headers) => fetchJson(p, { method: 'PUT', body, headers }),
  patch: (p, body, headers) => fetchJson(p, { method: 'PATCH', body, headers }),
  delete: (p, headers) => fetchJson(p, { method: 'DELETE', headers }),
  getBaseUrl: () => resolveBase(),
};

export default apiClient;
